/**
 * RewardsService - Système de récompenses pour encourager les tâches
 *
 * Types de récompenses:
 * - Musique de concentration (playlists)
 * - Sessions de méditation
 * - Sons relaxants
 * - Breaks guidés
 */

// Catalogue des récompenses disponibles
export const REWARDS_CATALOG = {
  // === MUSIQUE DE CONCENTRATION ===
  music: {
    lofi_beats: {
      id: 'lofi_beats',
      name: 'Lo-Fi Beats',
      description: 'Musique lo-fi pour la concentration',
      icon: '🎵',
      category: 'music',
      cost: 50, // XP nécessaire pour débloquer
      duration: 30, // minutes
      type: 'spotify',
      url: 'https://open.spotify.com/playlist/0vvXsWCC9xrXsKd4FyS8kM',
      embedUrl: 'https://open.spotify.com/embed/playlist/0vvXsWCC9xrXsKd4FyS8kM',
      preview: '🎧 Beats relaxants pour travailler',
    },
    focus_electronic: {
      id: 'focus_electronic',
      name: 'Focus Electronic',
      description: 'Électro ambient pour le deep work',
      icon: '🎹',
      category: 'music',
      cost: 75,
      duration: 45,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=jfKfPfyJRdk',
      videoId: 'jfKfPfyJRdk',
      preview: '🎛️ Électro ambient sans paroles',
    },
    nature_sounds: {
      id: 'nature_sounds',
      name: 'Sons de la Nature',
      description: 'Pluie, forêt, océan...',
      icon: '🌧️',
      category: 'music',
      cost: 30,
      duration: 60,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=mPZkdNFkNps',
      videoId: 'mPZkdNFkNps',
      preview: '🌿 Ambiance naturelle apaisante',
    },
    classical_focus: {
      id: 'classical_focus',
      name: 'Classique Focus',
      description: 'Mozart, Bach pour la concentration',
      icon: '🎻',
      category: 'music',
      cost: 60,
      duration: 40,
      type: 'spotify',
      url: 'https://open.spotify.com/playlist/37i9dQZF1DWZeKCadgRdKQ',
      embedUrl: 'https://open.spotify.com/embed/playlist/37i9dQZF1DWZeKCadgRdKQ',
      preview: '🎼 Musique classique pour le cerveau',
    },
    gaming_music: {
      id: 'gaming_music',
      name: 'Gaming OST',
      description: 'Musiques de jeux vidéo épiques',
      icon: '🎮',
      category: 'music',
      cost: 80,
      duration: 45,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=kXF3VYYa5TI',
      videoId: 'kXF3VYYa5TI',
      preview: '🕹️ OST de jeux vidéo motivants',
    },
  },

  // === MÉDITATION ===
  meditation: {
    quick_breath: {
      id: 'quick_breath',
      name: 'Respiration 2 min',
      description: 'Exercice de respiration rapide',
      icon: '🫁',
      category: 'meditation',
      cost: 20,
      duration: 2,
      type: 'guided',
      steps: [
        { action: 'Inspire', duration: 4 },
        { action: 'Retiens', duration: 4 },
        { action: 'Expire', duration: 6 },
      ],
      cycles: 4,
      preview: '😮‍💨 Reset rapide du stress',
    },
    body_scan: {
      id: 'body_scan',
      name: 'Body Scan 5 min',
      description: 'Scan corporel pour relâcher les tensions',
      icon: '🧘',
      category: 'meditation',
      cost: 40,
      duration: 5,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=QS2yDmWk0vs',
      videoId: 'QS2yDmWk0vs',
      preview: '🙆 Détente musculaire complète',
    },
    focus_meditation: {
      id: 'focus_meditation',
      name: 'Méditation Focus',
      description: 'Recentrage avant une tâche difficile',
      icon: '🎯',
      category: 'meditation',
      cost: 50,
      duration: 7,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=inpok4MKVLM',
      videoId: 'inpok4MKVLM',
      preview: '🔮 Clarté mentale et focus',
    },
    gratitude: {
      id: 'gratitude',
      name: 'Gratitude 3 min',
      description: 'Moment de gratitude guidé',
      icon: '🙏',
      category: 'meditation',
      cost: 25,
      duration: 3,
      type: 'guided',
      prompts: [
        "Pense à 3 choses pour lesquelles tu es reconnaissant(e) aujourd'hui...",
        "Visualise un moment de joie récent...",
        "Envoie de la gratitude à quelqu'un qui t'a aidé...",
      ],
      preview: '💝 Boost de positivité',
    },
    sleep_prep: {
      id: 'sleep_prep',
      name: 'Prépa Sommeil',
      description: 'Transition douce vers le repos',
      icon: '😴',
      category: 'meditation',
      cost: 60,
      duration: 10,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=rvaqPPjtxng',
      videoId: 'rvaqPPjtxng',
      preview: '🌙 Pour bien dormir',
    },
  },

  // === BREAKS / PAUSES ===
  breaks: {
    stretch_break: {
      id: 'stretch_break',
      name: 'Étirements 3 min',
      description: 'Pause étirements pour le corps',
      icon: '🤸',
      category: 'breaks',
      cost: 25,
      duration: 3,
      type: 'guided',
      exercises: [
        { name: 'Cou', instruction: 'Tourne doucement la tête de gauche à droite', duration: 20 },
        { name: 'Épaules', instruction: 'Hausse et relâche les épaules 5 fois', duration: 20 },
        { name: 'Dos', instruction: 'Étire les bras vers le ciel et penche-toi', duration: 30 },
        { name: 'Poignets', instruction: 'Fais des cercles avec les poignets', duration: 20 },
        { name: 'Jambes', instruction: 'Lève-toi et secoue les jambes', duration: 30 },
      ],
      preview: '💪 Réactive ton corps',
    },
    eye_rest: {
      id: 'eye_rest',
      name: 'Pause Yeux 20-20',
      description: 'Règle 20-20-20 pour les yeux',
      icon: '👁️',
      category: 'breaks',
      cost: 15,
      duration: 1,
      type: 'guided',
      instruction: 'Regarde un objet à 20 pieds (6m) pendant 20 secondes',
      preview: '👀 Repos pour tes yeux',
    },
    dance_break: {
      id: 'dance_break',
      name: 'Dance Break 🕺',
      description: 'Bouge pendant 3 minutes !',
      icon: '💃',
      category: 'breaks',
      cost: 35,
      duration: 3,
      type: 'youtube',
      url: 'https://www.youtube.com/watch?v=ymigWt5TOV8',
      videoId: 'ymigWt5TOV8',
      preview: '🎉 Libère ton énergie !',
    },
    walk_break: {
      id: 'walk_break',
      name: 'Mini Marche',
      description: 'Fais quelques pas pour te dégourdir',
      icon: '🚶',
      category: 'breaks',
      cost: 20,
      duration: 5,
      type: 'timer',
      instruction: 'Lève-toi et marche un peu. Prends l\'air si possible !',
      preview: '🌞 Change d\'air',
    },
    hydration: {
      id: 'hydration',
      name: 'Pause Hydratation',
      description: 'Rappel pour boire de l\'eau',
      icon: '💧',
      category: 'breaks',
      cost: 10,
      duration: 1,
      type: 'reminder',
      instruction: 'Bois un grand verre d\'eau ! Ton cerveau te remerciera.',
      preview: '🚰 Hydrate-toi !',
    },
  },

  // === RÉCOMPENSES SPÉCIALES ===
  special: {
    victory_celebration: {
      id: 'victory_celebration',
      name: 'Célébration Victoire',
      description: 'Animation spéciale de célébration',
      icon: '🎊',
      category: 'special',
      cost: 100,
      duration: 0,
      type: 'animation',
      preview: '🎉 Tu l\'as mérité !',
    },
    custom_theme: {
      id: 'custom_theme',
      name: 'Thème Custom',
      description: 'Débloque un thème de couleurs',
      icon: '🎨',
      category: 'special',
      cost: 200,
      duration: 0,
      type: 'unlock',
      preview: '✨ Personnalise ton app',
    },
    motivational_quote: {
      id: 'motivational_quote',
      name: 'Citation du Jour',
      description: 'Citation motivante personnalisée',
      icon: '💬',
      category: 'special',
      cost: 15,
      duration: 0,
      type: 'quote',
      quotes: [
        "Le secret pour avancer, c'est de commencer.",
        "Chaque expert a d'abord été un débutant.",
        "Tu es plus fort(e) que tu ne le penses.",
        "Un pas à la fois, tu y arrives.",
        "La perfection n'existe pas, l'action oui.",
        "Ton TDAH est un super-pouvoir quand tu le maîtrises.",
        "Les petites victoires font les grandes réussites.",
        "Aujourd'hui est un nouveau départ.",
      ],
      preview: '📜 Inspiration instantanée',
    },
  },
};

// Catégories de récompenses
export const REWARD_CATEGORIES = {
  music: { name: 'Musique', icon: '🎵', color: 'from-purple-500 to-pink-500' },
  meditation: { name: 'Méditation', icon: '🧘', color: 'from-cyan-500 to-blue-500' },
  breaks: { name: 'Pauses', icon: '☕', color: 'from-green-500 to-emerald-500' },
  special: { name: 'Spécial', icon: '⭐', color: 'from-yellow-500 to-orange-500' },
};

// Service de gestion des récompenses
class RewardsService {
  constructor() {
    this.catalog = REWARDS_CATALOG;
    this.categories = REWARD_CATEGORIES;
  }

  // Obtenir toutes les récompenses d'une catégorie
  getByCategory(category) {
    return Object.values(this.catalog[category] || {});
  }

  // Obtenir une récompense par ID
  getById(id) {
    for (const category of Object.values(this.catalog)) {
      if (category[id]) return category[id];
    }
    return null;
  }

  // Obtenir toutes les récompenses
  getAll() {
    const all = [];
    for (const category of Object.values(this.catalog)) {
      all.push(...Object.values(category));
    }
    return all;
  }

  // Obtenir les récompenses abordables (basé sur XP disponible)
  getAffordable(availableXP) {
    return this.getAll().filter(r => r.cost <= availableXP);
  }

  // Obtenir les récompenses recommandées (basé sur contexte)
  getRecommended(context = {}) {
    const { timeOfDay, mood, lastActivity } = context;
    const recommendations = [];

    // Recommandations basées sur l'heure
    const hour = timeOfDay || new Date().getHours();

    if (hour >= 6 && hour < 9) {
      // Matin - méditation et musique énergique
      recommendations.push(this.catalog.meditation.quick_breath);
      recommendations.push(this.catalog.music.lofi_beats);
    } else if (hour >= 9 && hour < 12) {
      // Matinée - focus
      recommendations.push(this.catalog.music.focus_electronic);
      recommendations.push(this.catalog.meditation.focus_meditation);
    } else if (hour >= 12 && hour < 14) {
      // Midi - pause
      recommendations.push(this.catalog.breaks.stretch_break);
      recommendations.push(this.catalog.breaks.hydration);
    } else if (hour >= 14 && hour < 17) {
      // Après-midi - maintenir l'énergie
      recommendations.push(this.catalog.music.gaming_music);
      recommendations.push(this.catalog.breaks.dance_break);
    } else if (hour >= 17 && hour < 21) {
      // Soirée - transition
      recommendations.push(this.catalog.music.nature_sounds);
      recommendations.push(this.catalog.meditation.body_scan);
    } else {
      // Nuit - préparation sommeil
      recommendations.push(this.catalog.meditation.sleep_prep);
      recommendations.push(this.catalog.meditation.gratitude);
    }

    // Filtrer les nulls et retourner max 4
    return recommendations.filter(Boolean).slice(0, 4);
  }

  // Vérifier si une récompense peut être utilisée
  canUse(rewardId, userXP, unlockedRewards = []) {
    const reward = this.getById(rewardId);
    if (!reward) return { canUse: false, reason: 'Récompense introuvable' };

    // Les récompenses permanentes n'ont besoin d'être débloquées qu'une fois
    const isPermanent = reward.type === 'unlock';
    if (isPermanent && !unlockedRewards.includes(rewardId)) {
      if (userXP < reward.cost) {
        return { canUse: false, reason: `Il te faut ${reward.cost - userXP} XP de plus` };
      }
    }

    return { canUse: true };
  }

  // Obtenir le coût en XP pour utiliser une récompense
  getCost(rewardId) {
    const reward = this.getById(rewardId);
    return reward?.cost || 0;
  }

  // Générer une citation aléatoire
  getRandomQuote() {
    const quotes = this.catalog.special.motivational_quote.quotes;
    return quotes[Math.floor(Math.random() * quotes.length)];
  }
}

export const rewardsService = new RewardsService();
export default rewardsService;
